/* ===========================================================
   editor/Toolbar.jsx — the formatting bar

   Casual: the everyday set (styles, bold/italic/underline, highlight,
   link, lists, checklist, align) and an Insert menu with table,
   picture, equation, divider and quote. Developer adds code blocks,
   runnable code, the live sandbox and a Markdown source toggle.
   Scrolls sideways on phones; every button has a name for screen
   readers and a shortcut hint in its tooltip.
   =========================================================== */

import { useEffect, useState } from 'preact/hooks';
import { IconButton, Menu, Icon, promptDialog, toast } from '../ui/ui.jsx';
import { insertImages } from './EditorView.jsx';
import { pickImage } from './images.js';
import { newSandbox } from './nodes/sandbox.js';
import { editMath } from './nodes/math.js';
import { env } from './env.js';

const mod = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

export function useEditorTick(editor) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const f = () => tick(n => n + 1);
    editor.on('transaction', f);
    editor.on('focus', f);
    return () => { editor.off('transaction', f); editor.off('focus', f); };
  }, [editor]);
}

export async function askLink(editor) {
  const current = editor.getAttributes('link').href || '';
  const url = await promptDialog({ title: current ? 'Edit link' : 'Add a link', label: 'Web address', value: current, placeholder: 'https://…', ok: current ? 'Save' : 'Add link' });
  if (url === null) return;
  const clean = url.trim();
  if (!clean) { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
  const href = /^(https?:|mailto:)/i.test(clean) ? clean : `https://${clean}`;
  if (editor.state.selection.empty && !current) {
    editor.chain().focus().insertContent({ type: 'text', text: clean, marks: [{ type: 'link', attrs: { href } }] }).run();
  } else {
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
  }
}

export async function insertEquation(editor, display = true) {
  const r = await env.editEquation({ latex: '', display, fresh: true });
  if (!r) return;
  const { latex } = r;
  if (!latex.trim()) return;
  editor.chain().focus().insertContent({ type: r.display ? 'mathBlock' : 'mathInline', attrs: { latex } }).run();
}

export async function addPicture(editor, docId) {
  const file = await pickImage();
  if (file) insertImages(editor, docId, [file]);
}

const STYLES = [
  { id: 'p', label: 'Normal text' },
  { id: 'h1', label: 'Title', level: 1 },
  { id: 'h2', label: 'Heading', level: 2 },
  { id: 'h3', label: 'Subheading', level: 3 },
  { id: 'h4', label: 'Small heading', level: 4, dev: true },
];

export function Toolbar({ editor, mode, docId, sourceOn, onToggleSource }) {
  useEditorTick(editor);
  if (!editor) return <div class="toolbar" role="toolbar" aria-label="Formatting" />;
  const dev = mode === 'dev';
  const ed = editor;
  const off = !ed.isEditable || sourceOn;
  const is = (n, a) => ed.isActive(n, a);
  const run = fn => () => fn(ed.chain().focus()).run();
  const style = STYLES.find(s => s.level && is('heading', { level: s.level }))?.id || 'p';
  const align = ['center', 'right'].find(a => ed.isActive({ textAlign: a })) || 'left';

  const insertItems = [
    { label: 'Table', icon: 'table', onSelect: () => ed.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
    { label: 'Picture…', icon: 'image', onSelect: () => addPicture(ed, docId) },
    { label: 'Picture from a link…', icon: 'link', onSelect: async () => {
      const url = await promptDialog({ title: 'Picture from a link', label: 'Picture address (https://…)', ok: 'Insert' });
      if (url && /^https:\/\//i.test(url.trim())) ed.chain().focus().setImage({ src: url.trim() }).run();
      else if (url) toast('That needs to be an https:// address.');
    } },
    { label: 'Equation…', icon: 'equation', onSelect: () => insertEquation(ed, true) },
    { label: 'Divider line', icon: 'divider', onSelect: () => ed.chain().focus().setHorizontalRule().run() },
    { label: 'Quote', icon: 'quote', onSelect: () => ed.chain().focus().toggleBlockquote().run() },
    ...(dev ? [
      'sep',
      { heading: 'Developer' },
      { label: 'Code block', icon: 'code', onSelect: () => ed.chain().focus().setCodeBlock({ language: '' }).run() },
      { label: 'Python (runnable)', icon: 'play', onSelect: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'python' }, content: [{ type: 'text', text: 'print("Hello from Python")' }] }).run() },
      { label: 'JavaScript (runnable)', icon: 'play', onSelect: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'javascript' }, content: [{ type: 'text', text: "console.log('Hello from JavaScript')" }] }).run() },
      { label: 'SQL (runnable)', icon: 'play', onSelect: () => ed.chain().focus().insertContent({ type: 'codeBlock', attrs: { language: 'sql' }, content: [{ type: 'text', text: 'SELECT 1 + 1 AS answer;' }] }).run() },
      { label: 'Live HTML/CSS/JS sandbox', icon: 'sandbox', onSelect: () => ed.chain().focus().insertContent(newSandbox()).run() },
      { label: 'Inline equation…', icon: 'equation', onSelect: () => insertEquation(ed, false) },
    ] : []),
  ];

  return (
    <div class={`toolbar ${off ? 'is-off' : ''}`} role="toolbar" aria-label="Formatting">
      <div class="tb-group">
        <IconButton icon="undo" label={`Undo (${mod} Z)`} disabled={off} onClick={run(c => c.undo())} />
        <IconButton icon="redo" label={`Redo (${mod} Y)`} disabled={off} onClick={run(c => c.redo())} />
      </div>
      <div class="tb-group">
        <select class="tb-style" aria-label="Text style" disabled={off} value={style}
          onChange={e => {
            const s = STYLES.find(x => x.id === e.target.value);
            const c = ed.chain().focus();
            (s.level ? c.setHeading({ level: s.level }) : c.setParagraph()).run();
          }}>
          {STYLES.filter(s => !s.dev || dev).map(s => <option value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div class="tb-group">
        <IconButton icon="bold" label={`Bold (${mod} B)`} active={is('bold')} disabled={off} onClick={run(c => c.toggleBold())} />
        <IconButton icon="italic" label={`Italic (${mod} I)`} active={is('italic')} disabled={off} onClick={run(c => c.toggleItalic())} />
        <IconButton icon="underline" label={`Underline (${mod} U)`} active={is('underline')} disabled={off} onClick={run(c => c.toggleUnderline())} />
        <IconButton icon="strike" label={`Strikethrough (${mod} Shift X)`} active={is('strike')} disabled={off} onClick={run(c => c.toggleStrike())} class="hide-sm" />
        <IconButton icon="highlight" label={`Highlight (${mod} Shift H)`} active={is('highlight')} disabled={off} onClick={run(c => c.toggleHighlight())} />
        <IconButton icon="link" label="Link" active={is('link')} disabled={off} onClick={() => askLink(ed)} />
        {dev && <IconButton icon="code" label={`Inline code (${mod} E)`} active={is('code')} disabled={off} onClick={run(c => c.toggleCode())} />}
      </div>
      <div class="tb-group">
        <IconButton icon="bullets" label={`Bulleted list (${mod} Shift 8)`} active={is('bulletList')} disabled={off} onClick={run(c => c.toggleBulletList())} />
        <IconButton icon="numbers" label={`Numbered list (${mod} Shift 7)`} active={is('orderedList')} disabled={off} onClick={run(c => c.toggleOrderedList())} />
        <IconButton icon="checklist" label={`Checklist (${mod} Shift 9)`} active={is('taskList')} disabled={off} onClick={run(c => c.toggleTaskList())} />
        <Menu label="Alignment" trigger={<IconButton icon={align === 'center' ? 'alignCenter' : align === 'right' ? 'alignRight' : 'alignLeft'} label="Align text" disabled={off} />}
          items={[
            { label: 'Left', icon: 'alignLeft', active: align === 'left', onSelect: run(c => c.setTextAlign('left')) },
            { label: 'Center', icon: 'alignCenter', active: align === 'center', onSelect: run(c => c.setTextAlign('center')) },
            { label: 'Right', icon: 'alignRight', active: align === 'right', onSelect: run(c => c.setTextAlign('right')) },
          ]} />
      </div>
      <div class="tb-group">
        <Menu label="Insert" trigger={<button type="button" class="tb-insert" disabled={off}><Icon name="plus" size={16} /><span>Insert</span><Icon name="chevron" size={14} /></button>} items={insertItems} />
        <IconButton icon="clearFormat" label="Clear formatting" disabled={off} class="hide-sm" onClick={run(c => c.unsetAllMarks().clearNodes())} />
      </div>
      {is('table') && !off && (
        <div class="tb-group tb-table">
          <Menu label="Table" trigger={<button type="button" class="tb-insert"><Icon name="table" size={16} /><span>Table</span><Icon name="chevron" size={14} /></button>}
            items={[
              { label: 'Add row below', onSelect: run(c => c.addRowAfter()) },
              { label: 'Add row above', onSelect: run(c => c.addRowBefore()) },
              { label: 'Add column right', onSelect: run(c => c.addColumnAfter()) },
              { label: 'Add column left', onSelect: run(c => c.addColumnBefore()) },
              'sep',
              { label: 'Delete row', onSelect: run(c => c.deleteRow()) },
              { label: 'Delete column', onSelect: run(c => c.deleteColumn()) },
              { label: 'Delete table', danger: true, onSelect: run(c => c.deleteTable()) },
            ]} />
        </div>
      )}
      {dev && (
        <div class="tb-group tb-end">
          <IconButton icon="markdown" label="Markdown source" active={!!sourceOn} onClick={onToggleSource} disabled={!ed.isEditable} />
        </div>
      )}
    </div>
  );
}

export { editMath };
